import React from 'react';
import {
  formatInvoiceQuantity,
  invoiceAddonName,
  splitInvoiceLines,
} from '../../../utils/invoiceFormat';

const money = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '0.00';
  return v.toFixed(2);
};

/**
 * Live + print-friendly invoice/receipt preview (no API calls).
 */
const InvoiceLivePreview = ({
  vendorLabel,
  vendorLogoUrl,
  vendorAddressLines = [],
  billToName,
  billToEmail,
  billToPhone,
  invoiceNumberPreview,
  invoiceDate,
  dueDate,
  lineItems,
  taxRate,
  subtotal,
  discountAmount = 0,
  discountType,
  discountValue,
  taxAmount,
  total,
  notes,
  paymentTerms,
  /** Optional structured plan rows: { label, percentage, amount, dueHint } */
  paymentPlanItems = [],
  bookingRef,
  statusLabel,
  documentType = 'invoice',
}) => {
  const isReceipt = documentType === 'receipt';
  const docLabel = isReceipt ? 'Payment Receipt' : 'Invoice';
  const numberLabel = isReceipt ? 'Receipt #' : 'Invoice #';
  const partyLabel = isReceipt ? 'Received from' : 'Bill to';
  const dateLabel = isReceipt ? 'Date' : 'Invoice date';
  const totalLabel = isReceipt ? 'Total Paid' : 'Total';
  const showDueDate = !isReceipt && dueDate;
  const planItems = Array.isArray(paymentPlanItems) ? paymentPlanItems : [];
  const showPaymentPlan = !isReceipt && planItems.length > 0;
  // Prefer structured plan on preview; fall back to free-text terms when no plan rows.
  const showPaymentTerms = !isReceipt && !showPaymentPlan && Boolean(paymentTerms);
  const isPaid = String(statusLabel || '').toLowerCase() === 'paid';
  const hasDiscount = Number(discountAmount) > 0;
  const { lines: serviceLines, addons: addonLines } = splitInvoiceLines(lineItems);
  const renderMoneyRow = (row, key, addon) => (
    <tr key={key} className="border-b border-slate-100">
      <td className="py-4 pr-3 pl-0 text-slate-800">
        {addon ? invoiceAddonName(row) : row.description || '—'}
      </td>
      <td className="py-4 pr-3 text-right tabular-nums">{formatInvoiceQuantity(row.quantity)}</td>
      <td className="py-4 pr-3 text-right tabular-nums">${money(row.unit_price)}</td>
      <td className="py-4 pl-2 text-right tabular-nums font-medium">
        ${money(Number(row.quantity) * Number(row.unit_price))}
      </td>
    </tr>
  );
  const discountLabel =
    discountType === 'percent' && Number(discountValue) > 0
      ? `Discount (${Number(discountValue)}%)`
      : 'Discount';

  return (
    <div className="relative bg-surface keep-light invoice-preview border border-slate-200 rounded-xl shadow-sm overflow-hidden text-slate-900 p-8 sm:p-10">
      {isReceipt && isPaid && (
        <div
          className="pointer-events-none absolute top-10 right-6 sm:right-10 rotate-[-18deg] border-2 border-emerald-600 text-emerald-700 font-bold tracking-widest uppercase px-4 py-1.5 text-sm opacity-80 select-none"
          aria-hidden
        >
          Paid
        </div>
      )}

      <div className={`pb-7 sm:pb-8 border-b border-slate-100 ${isReceipt ? 'text-center' : 'flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4'}`}>
        {isReceipt ? (
          <div className="flex flex-col items-center">
            {vendorLogoUrl && (
              <img
                src={vendorLogoUrl}
                alt={`${vendorLabel || 'Vendor'} logo`}
                className="h-14 w-14 rounded-lg object-cover border border-slate-200 mb-3"
              />
            )}
            <p className="text-lg sm:text-xl font-bold uppercase tracking-wide text-slate-900">{docLabel}</p>
            <h3 className="text-base font-medium text-slate-700 mt-1">{vendorLabel || 'Your business'}</h3>
            {vendorAddressLines.filter(Boolean).length > 0 && (
              <div className="mt-2 text-sm text-slate-600 space-y-0.5">
                {vendorAddressLines.filter(Boolean).map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            )}
            <div className="mt-3 text-sm text-slate-600">
              <span className="text-slate-500">{numberLabel}</span>{' '}
              <span className="font-mono font-medium text-slate-900">{invoiceNumberPreview}</span>
            </div>
            {bookingRef && (
              <div className="mt-1 text-xs text-slate-500">Booking #{bookingRef}</div>
            )}
          </div>
        ) : (
          <>
            <div>
              {vendorLogoUrl && (
                <img
                  src={vendorLogoUrl}
                  alt={`${vendorLabel || 'Vendor'} logo`}
                  className="h-12 w-12 rounded-lg object-cover border border-slate-200 mb-2"
                />
              )}
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{docLabel}</p>
              <h3 className="text-xl font-semibold text-slate-900 mt-1">{vendorLabel || 'Your business'}</h3>
              {vendorAddressLines.filter(Boolean).length > 0 && (
                <div className="mt-2 text-sm text-slate-600 space-y-0.5">
                  {vendorAddressLines.filter(Boolean).map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-left sm:text-right text-sm">
              <div className="text-slate-500">{numberLabel}</div>
              <div className="font-mono font-medium text-slate-900">{invoiceNumberPreview}</div>
              {statusLabel && (
                <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                  {statusLabel}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <div className="py-6 grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div className={isReceipt ? 'sm:col-span-1' : ''}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{partyLabel}</p>
          <div className="text-sm space-y-1">
            <div className="font-medium text-slate-900">{billToName || '—'}</div>
            {billToEmail && <div className="text-slate-600">{billToEmail}</div>}
            {billToPhone && <div className="text-slate-600">{billToPhone}</div>}
          </div>
        </div>
        <div className={`${isReceipt ? 'sm:text-left' : 'sm:text-right'} text-sm space-y-2`}>
          <div>
            <span className="text-slate-500">{dateLabel} </span>
            <span className="font-medium text-slate-900">{invoiceDate || '—'}</span>
          </div>
          {showDueDate && (
            <div>
              <span className="text-slate-500">Due date </span>
              <span className="font-medium text-slate-900">{dueDate}</span>
            </div>
          )}
          {!isReceipt && bookingRef && (
            <div className="text-xs text-slate-500 pt-1">Linked booking: {bookingRef}</div>
          )}
        </div>
      </div>

      <div className="pb-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-3 pr-3 pl-0">Description</th>
              <th className="py-3 pr-3 w-20 text-right">Qty</th>
              <th className="py-3 pr-3 w-28 text-right">Rate</th>
              <th className="py-3 w-28 text-right pl-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {serviceLines.map((row, idx) => renderMoneyRow(row, `line-${idx}`, false))}
            {addonLines.length > 0 ? (
              <tr>
                <td colSpan={4} className="pb-1 pt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Add-ons
                </td>
              </tr>
            ) : null}
            {addonLines.map((row, idx) => renderMoneyRow(row, `addon-${idx}`, true))}
          </tbody>
        </table>
      </div>

      <div className={`py-6 -mx-8 sm:-mx-10 px-8 sm:px-10 border-t border-slate-100 ${isReceipt ? 'bg-surface' : 'bg-slate-50'}`}>
        <div className="max-w-xs ml-auto space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Subtotal</span>
            <span className="tabular-nums font-medium">${money(subtotal)}</span>
          </div>
          {hasDiscount && (
            <div className="flex justify-between text-emerald-700">
              <span>{discountLabel}</span>
              <span className="tabular-nums font-medium">−${money(discountAmount)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-600">Tax {taxRate > 0 ? `(${taxRate}%)` : ''}</span>
            <span className="tabular-nums font-medium">${money(taxAmount)}</span>
          </div>
          <div className={`flex justify-between pt-2 border-t ${isReceipt ? 'border-slate-900' : 'border-slate-200'} text-base`}>
            <span className="font-semibold text-slate-900">{totalLabel}</span>
            <span className="tabular-nums font-bold text-slate-900">${money(total)}</span>
          </div>
        </div>
      </div>

      {(showPaymentPlan || showPaymentTerms || notes) && (
        <div className="pt-5 border-t border-slate-100 text-sm space-y-3">
          {showPaymentPlan && (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Payment plan</p>
              <ul className="space-y-2">
                {planItems.map((item, idx) => (
                  <li
                    key={`${item.label}-${idx}`}
                    className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">
                        {item.label}
                        {item.percentage != null ? (
                          <span className="ml-1.5 font-normal text-slate-500">
                            ({item.percentage}%)
                          </span>
                        ) : null}
                      </p>
                      {item.dueHint ? (
                        <p className="mt-0.5 text-xs text-slate-500">{item.dueHint}</p>
                      ) : null}
                    </div>
                    <span className="shrink-0 tabular-nums font-semibold text-slate-900">
                      ${money(item.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {showPaymentTerms && (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Payment terms</p>
              <p className="text-slate-700 whitespace-pre-wrap">{paymentTerms}</p>
            </div>
          )}
          {notes && (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-1">Notes</p>
              <p className="text-slate-700 whitespace-pre-wrap">{notes}</p>
            </div>
          )}
        </div>
      )}

      {isReceipt && (
        <div className="pt-6 mt-2 border-t border-slate-100 text-center text-sm text-slate-500 space-y-1">
          <p className="font-medium text-slate-700">Thank you for your business!</p>
          <p>This is an official receipt for your records.</p>
        </div>
      )}
    </div>
  );
};

export default InvoiceLivePreview;
